import React, { useEffect, useRef, useState } from "react";
import { webLoadingFalse } from "../../services/navigate.service";
import { getLocations } from "../../services/locations.services";
import { getItemMovementReport } from "../../services/report.service";
import { useResponsive } from "../../hook/useResponsive";
import ItemMovementReportIcon from "../../images/ItemMovementReportIcon";
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListScreen,
  TEXT,
  TEXT_MUTED,
  cells,
  t,
} from "../../components/list";

interface ItemMovementRow {
  /** Built client-side: the API rows carry no id. */
  rowId: string;
  reference?: string | null;
  referenceid?: string | null;
  productid?: string | null;
  companyName?: string | null;
  movementtypename?: string | null;
  sku?: string | null;
  productname?: string | null;
  currentqty?: string | null;
  adjustedqty?: string | null;
  updatedqty?: string | null;
  unitcost?: string | null;
  date?: string | null;
  group?: string | null;
}

interface ItemMovementResponse {
  count: number;
  data: Omit<ItemMovementRow, "rowId">[];
}

const toNumber = (value: string | null | undefined) =>
  value === null || value === undefined || value === "" ? null : Number(value);

const qtyCell = (value: string | null | undefined) => (
  <div
    className="font-inter"
    style={{ fontSize: "14px", color: TEXT, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
  >
    {value ?? ""}
  </div>
);

/** dd/MM/yyyy HH:mm in local time. */
const formatDateTime = (value: string | null | undefined) => {
  const d = new Date(value ?? "");
  if (!value || Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ItemMovementReport = () => {
  const { isTouchLayout } = useResponsive();
  const companyId = localStorage.getItem("companyId");
  const userId = localStorage.getItem("userId");
  const [language, setLanguage] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const inforTable: any = {
    img: <ItemMovementReportIcon />,
    title:
      language && language?.["No item movement report yet"]
        ? language?.["No item movement report yet"]
        : "No item movement report yet",
    desc:
      language && language?.["Once you've generated an item movement report, it will be displayed here."]
        ? language?.["Once you've generated an item movement report, it will be displayed here."]
        : "Once you've generated an item movement report, it will be displayed here.",
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const getListLocations = async () => {
    try {
      const response: any = await getLocations(userId, companyId, "", "", "");
      setLocations(response.data.data ?? []);
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };
  useEffect(() => {
    getListLocations();
    document.addEventListener("newToken", getListLocations);
    return () => {
      document.removeEventListener("newToken", getListLocations);
    };
  }, []);

  /** One page of movements; the API searches item name, SKU and sale / purchase invoice no. */
  const fetchItemMovements = async (query: ListQuery) => {
    webLoadingFalse();
    const response = await getItemMovementReport(
      query.page,
      query.limit,
      String(query.search ?? "").trim().replace(/^#/, ""),
      companyId,
      query.filters.type,
      query.filters.location,
      "",
    );
    const value: ItemMovementResponse = response.data;
    const rows: ItemMovementRow[] = (value.data ?? []).map((each, index) => ({
      ...each,
      rowId: `${each.referenceid ?? ""}:${each.productid ?? ""}:${each.date ?? ""}:${index}`,
    }));
    return { rows, count: value.count ?? 0 };
  };

  const movementLabel = (r: ItemMovementRow) =>
    r.movementtypename ? t(language, r.movementtypename, r.movementtypename) : "";

  const columns: ListColumn<ItemMovementRow>[] = [
    {
      key: "reference",
      label: "Ref.",
      width: 110,
      hideable: false,
      value: (r) => (r.reference ? "#" + r.reference : ""),
      mobile: "subtitle",
    },
    {
      key: "companyName",
      label: "Company Name",
      width: 170,
      value: (r) => r.companyName ?? "",
      mobile: "sheet",
    },
    {
      key: "movementtypename",
      label: "Movement",
      width: 130,
      minWidth: 100,
      render: cells.typePill((r) => movementLabel(r)),
      value: (r) => movementLabel(r),
      mobile: "trailing",
    },
    {
      key: "sku",
      label: "Sku",
      width: 120,
      value: (r) => r.sku ?? "",
      mobile: "sheet",
    },
    {
      key: "productname",
      label: "Item Name",
      width: 220,
      minWidth: 140,
      value: (r) => r.productname || r.sku || "",
      mobile: "title",
    },
    {
      key: "currentqty",
      label: "Current Qty.",
      width: 110,
      align: "right",
      render: (r) => qtyCell(r.currentqty),
      mobile: "sheet",
    },
    {
      key: "adjustedqty",
      label: "Adjusted",
      width: 100,
      align: "right",
      render: (r) => qtyCell(r.adjustedqty),
      mobile: "sheet",
    },
    {
      key: "updatedqty",
      label: "New Qty.",
      width: 100,
      align: "right",
      render: (r) => qtyCell(r.updatedqty),
      mobile: "sheet",
    },
    {
      key: "unitcost",
      label: "Unit Cost",
      width: 110,
      minWidth: 90,
      align: "right",
      render: cells.money((r) => toNumber(r.unitcost)),
      mobile: "sheet",
    },
    {
      key: "date",
      label: "Date",
      width: 140,
      minWidth: 110,
      render: (r) => (
        <div
          className="font-inter"
          style={{ fontSize: "13px", color: TEXT_MUTED, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {formatDateTime(r.date)}
        </div>
      ),
      value: (r) => formatDateTime(r.date),
      mobile: "subtitle",
    },
    {
      key: "qtyChange",
      label: "Quantity",
      width: 120,
      desktop: false,
      mobile: "trailing",
      render: (r) => (
        <div
          className="font-inter"
          style={{ fontSize: "12.5px", color: TEXT_MUTED, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {(r.currentqty ?? "") + " → " + (r.updatedqty ?? "")}
        </div>
      ),
    },
  ];

  const filters: ListFilter[] = [
    {
      key: "type",
      label: "Type",
      kind: "select",
      // The API matches "purchases" / "refunds"; the old "purchase" / "refund" values matched nothing.
      options: [
        { value: "sales", label: t(language, "Sales", "Sales") },
        { value: "purchases", label: t(language, "Purchases", "Purchases") },
        { value: "refunds", label: t(language, "Refund", "Refund") },
      ],
    },
    {
      key: "location",
      label: "Location",
      kind: "select",
      options: locations.map((l: any) => ({ value: String(l?.id), label: l?.name ?? "" })),
    },
  ];

  return (
    <div
      className="h-full relative"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: isTouchLayout ? "auto" : undefined,
        paddingBottom: isTouchLayout ? "24px" : undefined,
      }}
    >
      <div>
        <p
          style={{ color: "#222525" }}
          className="text-[24px] leading-[130%] font-medium font-inter"
        >
          {language && language?.["Item Movement Report"]
            ? language?.["Item Movement Report"]
            : "Item Movement Report"}
        </p>
      </div>
      <ListScreen
        language={language}
        columns={columns}
        fetchRows={fetchItemMovements}
        rowKey={(r: ItemMovementRow) => r.rowId}
        searchPlaceholder="Search item, SKU or invoice"
        searchInputRef={searchInputRef}
        filters={filters}
        countNoun="movements"
        emptyState={inforTable}
      />
    </div>
  );
};

export default ItemMovementReport;
